// src/utils/menu/menuMetrics.js
// Shared product metrics used by the flower menu and wholesale detail display.
// Rules confirmed 2026-10-07
// (design_intake/001-flower-menu-pdf/INTAKE.md).
import { TERPENE_KEYS, CANNABINOID_KEYS } from "../coaPanelStatus";

// Left-to-right order of colors on the menu: legend rows and cards within a section.
export const MENU_COLORS = ["yellow", "green", "blue", "navy", "purple"];

export const EFFECT_BY_COLOR = {
    yellow: "Energetic",
    green: "Awake",
    blue: "Calm",
    navy: "Relaxed",
    purple: "Sleepy",
};

export const AROMA_BY_COLOR = {
    yellow: "Chemical",
    green: "Floral",
    blue: "Fuel",
    navy: "Fruity",
    purple: "Earthy",
};

export const POTENCY_TIERS = {
    light: { bars: 1, label: "Light" },
    regular: { bars: 2, label: "Regular" },
    extra: { bars: 3, label: "Extra Strength" },
};

export const DEMS_TIERS = {
    low: { code: "DEMS-L", label: "Low" },
    medium: { code: "DEMS-M", label: "Medium" },
    high: { code: "DEMS-H", label: "High" },
};

const POTENCY_REGULAR_MIN = 20;
const POTENCY_REGULAR_MAX = 27;
const DEMS_MEDIUM_MIN = 0.06;
const DEMS_MEDIUM_MAX = 0.13;

const toNumber = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? null : n;
};

// Rounding before a threshold comparison keeps float noise (e.g. 3.9 / 30 =
// 0.13000000000000003) from pushing a boundary value into the wrong tier.
const round = (value, places) => {
    const factor = 10 ** places;
    return Math.round(value * factor) / factor;
};

function sumKeys(coa, keys) {
    let total = 0;
    let hasValue = false;
    for (const key of keys) {
        const n = toNumber(coa[key]);
        if (n == null) continue;
        hasValue = true;
        total += n;
    }
    return { total, hasValue };
}

export function menuColor(product) {
    const color = (product.color || "").toLowerCase();
    return MENU_COLORS.includes(color) ? color : null;
}

export function aromaForColor(color) {
    return AROMA_BY_COLOR[(color || "").toLowerCase()] || null;
}

/**
 * Total THC % = THCa x 0.877 + delta-9 THC.
 * A blank delta-9 alone counts as 0 (labs report a non-detect as blank).
 *
 * @returns {number|null} null when neither THCa nor delta-9 THC was reported.
 */
export function totalThc(coa) {
    const thca = toNumber(coa.THCa);
    const d9 = toNumber(coa.delta_9_THC);
    if (thca == null && d9 == null) return null;
    return round((thca || 0) * 0.877 + (d9 || 0), 2);
}

/**
 * Below 20 Light, 20 to 27 inclusive Regular, above 27 Extra Strength.
 *
 * @returns {"light"|"regular"|"extra"|null}
 */
export function potencyTier(coa) {
    const thc = totalThc(coa);
    if (thc == null) return null;
    if (thc < POTENCY_REGULAR_MIN) return "light";
    if (thc <= POTENCY_REGULAR_MAX) return "regular";
    return "extra";
}

/**
 * DEMS ratio = total terpenes / total cannabinoids, using the same column sets
 * as the Inventory table's terpene-to-cannabinoid ratio.
 *
 * @returns {number|null} null when there are no terpene values or no cannabinoids.
 */
export function demsRatio(coa) {
    const terpenes = sumKeys(coa, TERPENE_KEYS);
    const cannabinoids = sumKeys(coa, CANNABINOID_KEYS);
    if (!terpenes.hasValue || cannabinoids.total <= 0) return null;
    return round(terpenes.total / cannabinoids.total, 4);
}

/**
 * Below 0.06 Low, 0.06 to 0.13 inclusive Medium, above 0.13 High.
 *
 * @returns {"low"|"medium"|"high"|null}
 */
export function demsTier(coa) {
    const ratio = demsRatio(coa);
    if (ratio == null) return null;
    if (ratio < DEMS_MEDIUM_MIN) return "low";
    if (ratio <= DEMS_MEDIUM_MAX) return "medium";
    return "high";
}
